/**
 * Merge client CSV parse errors with Pacific pre-flight errors for the same row.
 * When both layers flag the same rule (different wording), the client message is
 * kept — same policy as {@link mergeWarningsByIndex} for R13 warnings.
 */

const SERVER_ERROR_PART_SEPARATOR = "; ";

/** Rules enforced in both the CSV parser and Pacific `validator.py`. */
type SharedErrorKind =
  | "blankWorkPatternName"
  | "duplicateWorkPatternNameInBatch"
  | "allZeroDayHours"
  | "outOfRangeDayHours"
  | "dayHoursLengthMismatch"
  | "invalidPayPublicHolidays"
  | "cycleDaysOutOfRange";

const INVALID_PAY_PUBLIC_HOLIDAYS_PREFIX =
  /^(?:payPublicHolidays|PayPublicHolidays) must be 'Yes' or 'No', got /i;

const CYCLE_DAYS_SERVER_RANGE_PREFIX =
  /^cycleDays must be between 1 and 365 inclusive, got /i;

const CYCLE_DAYS_CLIENT_RANGE_PREFIX =
  /^CycleDays must be (?:365 or fewer|a whole number between 1 and 365), got /i;

function isInvalidPayPublicHolidaysMessage(message: string): boolean {
  return INVALID_PAY_PUBLIC_HOLIDAYS_PREFIX.test(message.trim());
}

function isCycleDaysOutOfRangeMessage(message: string): boolean {
  const trimmed = message.trim().replace(/\.$/, "");
  return (
    CYCLE_DAYS_SERVER_RANGE_PREFIX.test(trimmed) ||
    CYCLE_DAYS_CLIENT_RANGE_PREFIX.test(trimmed)
  );
}

/** Client or Pacific wording when `cycleDays` is outside 1–365 (not a per-day 0–24 error). */
export function isCycleDaysOutOfRangeErrorMessage(message: string): boolean {
  return isCycleDaysOutOfRangeMessage(message);
}

/**
 * Matches the client parser's out-of-range day-hour message produced by
 * `validateDayHoursFromStrings`: "DayN value 'X' is out of range — must be between 0 and 24".
 */
function isClientOutOfRangeDayHoursMessage(message: string): boolean {
  return message.includes("is out of range — must be between 0 and 24");
}

/**
 * Matches the client parser's day-hours-count-vs-CycleDays mismatch produced by
 * `parseDayHours` (#474): "header has N Day column(s) but CycleDays is M".
 */
function isClientDayHoursLengthMismatchMessage(message: string): boolean {
  return /header has \d+ Day column\(s\) but CycleDays is \d+/i.test(message);
}

/**
 * Matches the Pacific server's day-hours-length mismatch produced by
 * `validator.py` R1: "dayHours length (N) must equal cycleDays (M)."
 */
function isServerDayHoursLengthMismatchMessage(message: string): boolean {
  return /^dayHours length \(\d+\) must equal cycleDays \(\d+\)\.?$/i.test(
    message.trim()
  );
}

/** Client or Pacific wording for cycle day count vs `dayHours` / Day columns (#474). */
export function isDayHoursLengthMismatchErrorMessage(message: string): boolean {
  const trimmed = message.trim();
  return (
    isClientDayHoursLengthMismatchMessage(trimmed) ||
    isServerDayHoursLengthMismatchMessage(trimmed)
  );
}

/**
 * Matches the Pacific server's out-of-range day-hour message produced by
 * `validator.py` R19: "dayHours values must be between 0 and 24 inclusive; found out-of-range value(s) [...]."
 * Note: the server message itself contains "; " which causes `splitServerErrorParts`
 * to split it into two halves — we match both halves here so both are suppressed
 * when the client already has an equivalent message.
 */
function isServerOutOfRangeDayHoursMessage(message: string): boolean {
  const trimmed = message.trim();
  return (
    /^dayHours values must be between \d+ and \d+ inclusive$/i.test(trimmed) ||
    /^found out-of-range value\(s\)/i.test(trimmed)
  );
}

function clientSharedErrorKind(message: string): SharedErrorKind | null {
  if (message === "WorkPatternName is required") {
    return "blankWorkPatternName";
  }
  if (message.includes("Duplicate workPatternName")) {
    return "duplicateWorkPatternNameInBatch";
  }
  if (message.includes("At least one day must have hours greater than 0")) {
    return "allZeroDayHours";
  }
  if (isClientOutOfRangeDayHoursMessage(message)) {
    return "outOfRangeDayHours";
  }
  if (isClientDayHoursLengthMismatchMessage(message)) {
    return "dayHoursLengthMismatch";
  }
  if (isInvalidPayPublicHolidaysMessage(message)) {
    return "invalidPayPublicHolidays";
  }
  if (isCycleDaysOutOfRangeMessage(message)) {
    return "cycleDaysOutOfRange";
  }
  return null;
}

function serverSharedErrorKind(message: string): SharedErrorKind | null {
  const trimmed = message.trim();
  if (/^workPatternName must not be blank\.?$/i.test(trimmed)) {
    return "blankWorkPatternName";
  }
  if (message.includes("Duplicate workPatternName")) {
    return "duplicateWorkPatternNameInBatch";
  }
  if (
    trimmed === "At least one day must have hours greater than 0." ||
    trimmed === "At least one day must have hours greater than 0"
  ) {
    return "allZeroDayHours";
  }
  if (isServerOutOfRangeDayHoursMessage(message)) {
    return "outOfRangeDayHours";
  }
  if (isServerDayHoursLengthMismatchMessage(message)) {
    return "dayHoursLengthMismatch";
  }
  if (isInvalidPayPublicHolidaysMessage(message)) {
    return "invalidPayPublicHolidays";
  }
  if (isCycleDaysOutOfRangeMessage(message)) {
    return "cycleDaysOutOfRange";
  }
  return null;
}

/** True when the client and server messages describe the same shared-layer rule. */
export function areRedundantClientServerErrorMessages(
  clientMessage: string,
  serverMessage: string
): boolean {
  const clientKind = clientSharedErrorKind(clientMessage);
  const serverKind = serverSharedErrorKind(serverMessage);
  if (clientKind !== null && clientKind === serverKind) {
    return true;
  }
  // Pacific R1 length mismatch is a side effect when CycleDays is out of range (#486).
  if (
    clientKind === "cycleDaysOutOfRange" &&
    serverKind === "dayHoursLengthMismatch"
  ) {
    return true;
  }
  return false;
}

function splitServerErrorParts(serverError: string): string[] {
  return serverError
    .split(SERVER_ERROR_PART_SEPARATOR)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/**
 * Join Pacific and client errors for one row, dropping server wording when the
 * client already reported the same rule.
 */
export function mergeClientAndServerErrorParts(
  serverParts: readonly string[],
  clientParts: readonly string[]
): string[] {
  const keptServer = serverParts.filter(
    (serverPart) =>
      !clientParts.some((clientPart) =>
        areRedundantClientServerErrorMessages(clientPart, serverPart)
      )
  );
  return [...keptServer, ...clientParts];
}

export function mergeClientAndServerRowErrors(
  serverError: string | undefined,
  clientMessages: string[] | undefined
): string | undefined {
  const serverParts = serverError ? splitServerErrorParts(serverError) : [];
  const clientParts = clientMessages ?? [];
  const merged = mergeClientAndServerErrorParts(serverParts, clientParts);
  if (merged.length === 0) {
    return undefined;
  }
  return merged.join(SERVER_ERROR_PART_SEPARATOR);
}
